import noDataImage from "@app/assets/images/no-data.png";
import { URLS } from "@app/shared/urls";
import { type BreadcrumbItem } from "@design-system/components/Breadcrumb";
import { PageHeader } from "@design-system/components/PageHeader";
import { ArrowIcon, GlobeIcon } from "@design-system/icons";
import { trans } from "@mongez/localization";
import { Link, useNavigate } from "@remix-run/react";

export function NotFoundPage() {
  const navigate = useNavigate();

  const breadcrumbItems: BreadcrumbItem[] = [
    {
      label: "home",
      link: URLS.home,
    },
    {
      label: "notFound.title",
    },
  ];

  return (
    <div>
      <PageHeader
        translationKey="notFound.title"
        breadcrumbItems={breadcrumbItems}
        className="mb-6"
      >
        <p className="max-w-2xl leading-relaxed text-gray-200">
          {trans("notFound.description")}
        </p>
      </PageHeader>

      {/* Main Content */}
      <section className="relative -mt-12 lg:-mt-20">
        <div className="container mx-auto px-4 py-10 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">
            {/* Left Column - Illustration & Actions */}
            <div className="text-center lg:text-left">
              <div className="relative mx-auto mb-12 max-w-md lg:mx-0 lg:max-w-lg">
                <img
                  src={noDataImage}
                  alt="Page not found"
                  className="animate-float h-64 w-full object-contain opacity-75 drop-shadow-2xl"
                />
                <div className="absolute -right-4 -top-4 -skew-x-3 rounded-2xl bg-gradient-to-r from-orange-500 to-red-500 px-6 py-3 text-5xl font-black text-white shadow-2xl transition-transform duration-300 hover:skew-x-0 lg:-right-8 lg:-top-8">
                  404
                </div>
              </div>
            </div>

            {/* Right Column - Text & Actions */}
            <div className="lg:text-left">
              <div className="rounded-3xl border border-white/20 bg-white/60 p-8 shadow-2xl backdrop-blur-xl">
                <h3 className="mb-4 text-2xl font-bold text-gray-800">
                  {trans("notFound.needHelp")}
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center gap-3 rounded-xl bg-white/50 p-3 transition-colors hover:bg-white">
                    <ArrowIcon className="h-5 w-5 flex-shrink-0 text-orange-500" />
                    <span>{trans("notFound.checkPage")}</span>
                  </li>
                  <li className="flex items-center gap-3 rounded-xl bg-white/50 p-3 transition-colors hover:bg-white">
                    <GlobeIcon className="h-5 w-5 flex-shrink-0 text-blue-500" />
                    <span>{trans("notFound.returnToHomepage")}</span>
                  </li>
                </ul>

                <div className="flex flex-col justify-center gap-4 sm:flex-row lg:justify-start">
                  <Link
                    to={URLS.home}
                    className="hover:shadow-3xl flex-1 text-center whitespace-nowrap rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-600 px-8 py-4 text-lg font-semibold text-white shadow-2xl transition-all duration-300 hover:scale-105 hover:from-emerald-600 hover:to-emerald-700 sm:flex-none"
                  >
                    {trans("notFound.goHome")}
                  </Link>
                  <button
                    onClick={() => navigate(-1)}
                    className="flex-1 whitespace-nowrap rounded-2xl border-2 border-gray-200 bg-white px-8 py-4 text-lg font-semibold text-gray-800 shadow-xl transition-all duration-300 hover:scale-105 hover:border-gray-300 hover:shadow-2xl sm:flex-none"
                  >
                    {trans("notFound.goBack")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
